/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import { FALLBACK_LANGUAGES, FALLBACK_REGIONS, AppConstants } from '../shared/Constants';
import defaultLanguageNames from '../translations/languages.json';
import defaultTerritoryNames from '../translations/territories.json';
import { loadLanguageName, loadTerritoryNames } from '../services/apps/language';
import ArrayUtil from './ArrayUtil';

export const isDefaultLanguage = tag => tag && tag === AppConstants.DEFAULT_LANG;

export const getLanguageDisplayName = (localName, globalName) => {
  const standardLocalName = localName.trim().toLowerCase();
  const standardGlobalName = localName.trim().toLowerCase();
  return standardLocalName === standardGlobalName ? localName : `${localName} (${globalName})`;
};

const getAllTranslatedList = (translatedList, defaultLanguages, currentLocale) => {
  const translated = [];

  for (const tag in translatedList) {
    const fallbackTag = FALLBACK_LANGUAGES[tag] || tag;

    const translatedLang = translatedList[fallbackTag];
    if (translatedLang) {
      const exist = translated.find(trans => trans.tag === fallbackTag);

      if (!exist) {
        const languageName = isDefaultLanguage(currentLocale)
          ? `${translatedLang}`
          : getLanguageDisplayName(translatedLang, defaultLanguages[fallbackTag]);
        const langObj = {
          tag: fallbackTag,
          name: languageName,
        };
        translated.push(langObj);
      }
    }
  }

  const sorted = ArrayUtil.sortByProp(translated, 'tag');
  return sorted;
};

const getTranslatedListByTags = (translatedList, langTagList, defaultLanguages, currentLocale) => {
  const translated = [];
  langTagList.forEach((tag) => {
    const fallbackTag = FALLBACK_LANGUAGES[tag] || tag;

    const translatedLang = translatedList[fallbackTag];
    if (translatedLang) {
      const exist = translated.find(trans => trans.tag === fallbackTag);

      if (!exist) {
        const languageName = isDefaultLanguage(currentLocale)
          ? `${translatedLang}`
          : getLanguageDisplayName(translatedLang, defaultLanguages[fallbackTag]);
        const langObj = {
          tag: fallbackTag,
          name: languageName,
        };

        translated.push(langObj);
      }
    }
  });
  const sorted = ArrayUtil.sortByProp(translated, 'tag');

  return sorted;
};

export const translatedLanguages = (translatedList, langTagList, defaultLanguages, currentLocale) => {
  if (ArrayUtil.isNotEmpty(langTagList)) {
    return getTranslatedListByTags(translatedList, langTagList, defaultLanguages, currentLocale);
  }
  return getAllTranslatedList(translatedList, defaultLanguages, currentLocale);
};

export const getLanguageNames = (srcData, dataKey) => {
  const key = FALLBACK_REGIONS[dataKey] ? FALLBACK_REGIONS[dataKey] : dataKey;
  return srcData.main[key].localeDisplayNames.languages;
};

export const getTranslatedLanguages = async (locale) => {
  let translatedLanguage = [];
  const defaultLanguages = getLanguageNames(defaultLanguageNames, AppConstants.DEFAULT_LANG);
  try {
    translatedLanguage = getLanguageNames(await loadLanguageName(locale), locale);
  } catch (err) {
    translatedLanguage = defaultLanguages;
  }

  return translatedLanguage;
};

export const subLanguageList = (tagList, languageList) => {
  const languages = [];

  if (ArrayUtil.isNotEmpty(tagList)) {
    tagList.forEach((tag) => {
      let language = languageList.find(lang => lang.value === tag);
      if (!language) {
        const fallbackTag = FALLBACK_LANGUAGES[tag] || tag;
        language = languageList.find(lang => fallbackTag === lang.value);
      }

      if (language) {
        languages.push(language);
      }
    });
  }
  return languages;
};

const USER_DEFAULT_LANGUAGE = {
  value: 'en-Us',
  label: 'American English',
};

/** DEPRECATED: Once we cleanup the language tags, we can remove/ simply this! */
export const getUserLanguage = (value, languageList) => {
  let language = languageList.find(lang => lang.value === value);
  if (!language) {
    const fallbackTag = FALLBACK_LANGUAGES[value] || value;
    language = languageList.find(lang => lang.value === fallbackTag);
  }
  return language || USER_DEFAULT_LANGUAGE;
};

export const getTerritoryNames = (srcData, dataKey) => srcData.main[dataKey].localeDisplayNames.territories;
// const key = FALLBACK_REGIONS[dataKey] ? FALLBACK_REGIONS[dataKey] : dataKey;

export const territoriesToArray = (territories) => {
  const arr = [];
  for (const key in territories) {
    if ({}.hasOwnProperty.call(territories, key)) {
      arr.push({
        code: key,
        name: territories[key],
      });
    }
  }
  return arr;
};

export const getTranslatedTerritoryNames = async (locale) => {
  let territories = [];
  const defaultTerritories = getTerritoryNames(defaultTerritoryNames, AppConstants.DEFAULT_LANG);
  try {
    territories = getTerritoryNames(await loadTerritoryNames(locale), locale);
  } catch (err) {
    territories = defaultTerritories;
  }

  const terArr = territoriesToArray(territories);
  return terArr;
};

export const generalTemplateSupportedLanguages = (active = [], reportLangs = [], JPSLangs = []) => {
  const supported = [];

  if (ArrayUtil.isNotEmpty(active)) {
    active.forEach((lang) => {
      const existed = supported.find(sup => sup.value === lang.value);
      if (!existed) {
        supported.push(lang);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(reportLangs)) {
    reportLangs.forEach((lang) => {
      const existed = supported.find(sup => sup.value === lang.value);

      if (!existed) {
        supported.push(lang);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(JPSLangs)) {
    JPSLangs.forEach((lang) => {
      const existed = supported.find(sup => sup.value === lang.value);
      if (!existed) {
        supported.push(lang);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(supported)) {
    const sorted = supported.slice().sort((a, b) => (a.tag > b.tag ? 1 : -1));
    return sorted;
  }
  return supported;
};

export const surveyTemplateSupportedLanguages = (active = [], reportLangs = []) => {
  const supported = [];

  if (ArrayUtil.isNotEmpty(active)) {
    active.forEach((lang) => {
      const existed = supported.find(sup => sup.tag === lang.tag);
      if (!existed) {
        supported.push(lang);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(reportLangs)) {
    reportLangs.forEach((lang) => {
      const existed = supported.find(sup => sup.tag === lang.tag);
      if (!existed) {
        supported.push(lang);
      }
    });
  }

  if (ArrayUtil.isNotEmpty(supported)) {
    const sorted = supported.slice().sort((a, b) => (a.tag > b.tag ? 1 : -1));
    return sorted;
  }

  return supported;
};

export const fixedLanguage = (preferredLangage, languageList) => {
  if (!preferredLangage) {
    return AppConstants.DEFAULT_LANG;
  }
  const accountLanguage = languageList.find(lang => lang.value === preferredLangage);

  if (accountLanguage) {
    return accountLanguage.value;
  }

  const fallbackLang = FALLBACK_LANGUAGES[preferredLangage] || preferredLangage;

  if (fallbackLang) {
    const decidedLang = languageList.find(lang => lang.tag === fallbackLang);

    if (decidedLang) {
      return decidedLang.tag;
    }
  }
  return AppConstants.DEFAULT_LANG;
};

// DEPRECATED - inject translatedTags from i18n state or
// cldr object and use localeDisplayNames(tags)
const getCLDRListFromTags = (cldr, tags) => {
  const languageList = tags.map(tag => ({
    tag,
    name: cldr.main(`localeDisplayNames/languages/${tag}`),
  }));
  return languageList;
};

const getFullCLDRList = (cldr) => {
  const languageObject = cldr.main('localeDisplayNames/languages');
  const languageList = [];
  if (languageObject) {
    for (const tag in languageObject) {
      languageList.push({
        tag,
        name: languageObject[tag],
      });
    }
  }

  return languageList;
};

// DEPRECATED: inject translatedTags from i18n state or cldr object and use localeDisplayNames(tags)
export const getLanguageListFromCLDRObject = (cldr, tags = []) => {
  if (ArrayUtil.isNotEmpty(tags)) {
    return getCLDRListFromTags(cldr, tags);
  }

  return getFullCLDRList(cldr);
};
